/**
 * OCHAT OVERLAY: Ochat's translations, layered on top of upstream's.
 *
 * Ochat used to rewrite app/javascript/dashboard/i18n/locale/es/*.json directly
 * -- around 1550 keys across 14 files (rioplatense voseo, keys upstream leaves in
 * English, and its own MARKETING namespace). Upstream regenerates those exact
 * files from Crowdin every release, so every upgrade meant resolving thousands of
 * lines of JSON conflicts by hand, and any key upstream added inside a namespace
 * we had rewritten was silently lost.
 *
 * Now upstream owns locale/*, we own this directory, and the two are merged at
 * runtime.
 *
 * WHY A DEEP MERGE AND NOT A SPREAD
 * The locale objects are namespace -> tree ({ SETTINGS: { ... } }). A shallow
 * spread would replace a whole namespace, dropping every key upstream added
 * inside it and leaving blank strings in the UI. The merge has to walk down to
 * the leaves so that our overrides win per key and upstream's new keys survive.
 *
 * ADDING A TRANSLATION
 * Put it in ochat/i18n/<locale>/<namespace file>.json, mirroring upstream's file
 * names and key paths. Do not edit anything under dashboard/i18n/locale/.
 *
 * Brand names are NOT handled here: createI18n's postTranslation swaps
 * "Chatwoot" for the configured installation name across the whole catalogue.
 */
import en from './en';
import es from './es';

const OCHAT_MESSAGES = { en, es };

const isPlainObject = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * Recursively merges `override` into `base`, override winning at the leaves.
 * Returns new objects rather than mutating, so the imported locale modules stay
 * untouched (they are shared with the widget and survey bundles).
 */
export const deepMerge = (base, override) => {
  if (!isPlainObject(base) || !isPlainObject(override)) {
    return override === undefined ? base : override;
  }

  const mergedOverrides = Object.fromEntries(
    Object.entries(override).map(([key, value]) => [
      key,
      key in base ? deepMerge(base[key], value) : value,
    ])
  );

  return { ...base, ...mergedOverrides };
};

/**
 * Takes upstream's `{ locale: messages }` map and returns it with the Ochat
 * overrides layered in. Locales we do not override pass through untouched.
 */
export const applyOchatMessages = messages =>
  Object.entries(OCHAT_MESSAGES).reduce(
    (result, [locale, overrides]) =>
      result[locale]
        ? { ...result, [locale]: deepMerge(result[locale], overrides) }
        : result,
    { ...messages }
  );

export default applyOchatMessages;
